import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast
} from "@veritrellis/ui";
import { Copy, RotateCcw, Wand2 } from "lucide-react";
import type { BuilderState } from "./builderTypes.js";
import { policyModeLabel } from "../../lib/policyPresentation.js";

type PolicyJsonEditorProps = {
  jsonText: string;
  onChange: (raw: string) => void;
  jsonError: string | null;
  disabled?: boolean;
  builderCompatible: boolean;
  mode: BuilderState["mode"];
  onModeChange: (mode: BuilderState["mode"]) => void;
  onResetFromBuilder?: () => void;
};

const modes: BuilderState["mode"][] = ["conditional", "allow", "deny", "require_approval"];

export const PolicyJsonEditor = ({
  jsonText,
  onChange,
  jsonError,
  disabled = false,
  builderCompatible,
  mode,
  onModeChange,
  onResetFromBuilder
}: PolicyJsonEditorProps) => {
  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonText) as unknown;
      onChange(JSON.stringify(parsed, null, 2));
      toast.show({ title: "Formatted", message: "JSON prettified.", color: "green" });
    } catch {
      toast.show({ title: "Invalid JSON", message: "Fix syntax before formatting.", color: "red" });
    }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      toast.show({ title: "Copied", message: "Policy JSON copied.", color: "green" });
    } catch {
      toast.show({ title: "Failed", message: "Clipboard unavailable.", color: "red" });
    }
  };

  return (
    <div className="admin-json-editor">
      {!builderCompatible ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription>
            This policy uses constructs the visual editor cannot represent. Edit JSON here, then
            save.
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="admin-panel" aria-labelledby="policy-json-heading">
        <div className="admin-panel-header">
          <h2 id="policy-json-heading" className="admin-panel-title">
            Policy JSON
          </h2>
          <div className="admin-json-editor-badges">
            {builderCompatible ? (
              <Badge variant="success">Syncs with Rules</Badge>
            ) : (
              <Badge variant="warning">JSON only</Badge>
            )}
          </div>
        </div>

        <div className="admin-json-editor-toolbar">
          <Button type="button" variant="secondary" size="sm" onClick={() => void formatJson()}>
            <Wand2 style={{ width: 14, height: 14, marginRight: 6 }} />
            Format
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => void copyJson()}>
            <Copy style={{ width: 14, height: 14, marginRight: 6 }} />
            Copy
          </Button>
          {onResetFromBuilder != null ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={onResetFromBuilder}
            >
              <RotateCcw style={{ width: 14, height: 14, marginRight: 6 }} />
              Reset from rules
            </Button>
          ) : null}
        </div>

        <div className="admin-panel-body-padded" style={{ paddingTop: 0 }}>
          <p className="admin-form-field-hint" style={{ marginBottom: 10 }}>
            Canonical policy object sent to the API. Valid keys: mode, rules, default_decision,
            auto_allow_low_risk.
          </p>
          <div
            className={`admin-json-editor-surface${jsonError != null ? " admin-json-editor-surface-error" : ""}`}
          >
            <Textarea
              className="admin-json-textarea"
              disabled={disabled}
              spellCheck={false}
              value={jsonText}
              onChange={(e) => onChange(e.currentTarget.value)}
              aria-invalid={jsonError != null}
            />
          </div>
          {jsonError != null ? (
            <p className="admin-json-editor-error">{jsonError}</p>
          ) : (
            <p className="admin-json-editor-status">Valid JSON syntax</p>
          )}
        </div>
      </section>

      <section className="admin-panel" aria-labelledby="policy-json-shortcuts-heading">
        <div className="admin-panel-header">
          <h2 id="policy-json-shortcuts-heading" className="admin-panel-title">
            Shortcuts
          </h2>
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-form-field admin-form-field-full">
            <label className="admin-form-field-label" htmlFor="json-mode-shortcut">
              Top-level mode
            </label>
            <p className="admin-form-field-hint">
              Quick switch without editing JSON. Updates the Rules tab when compatible.
            </p>
            <div className="admin-form-field-control" style={{ maxWidth: 280 }}>
              <Select
                value={mode}
                onValueChange={(v) => onModeChange(v as BuilderState["mode"])}
                disabled={disabled}
              >
                <SelectTrigger id="json-mode-shortcut" style={{ height: 36, width: "100%" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {policyModeLabel[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
